import {useEffect, useRef, useCallback, useState} from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { DateTime } from "luxon";
import * as Yup from "yup";
import i18n from "i18n";

import useBuyFormStore from "../../stores/useBuyFormStore";
import useParkingInfoStore from "../../stores/useParkingInfoStore";
import useBuyUIStore from "../../stores/useBuyUIStore";

import useCardsState from "../../hooks/useCardsState";
import { useGetParkingZone } from "hooks/api/useGetParkingZone";
import { useGetParkingPrice } from "hooks/api/useGetParkingPrice";
import { useCurrentTime } from "hooks/useCurrentTime";

import Icon from "../../components/Icon";
import VehiclePicker from "../../components/Buy/VehiclePicker/VehiclePicker";
import EmailPicker from "../../components/Buy/EmailPicker/EmailPicker";
import FullscreenLoader from "../../components/FullscreenLoader/FullscreenLoader";
import CardSelection from "components/Buy/CardSelection/CardSelection";
import NoEmailWarningModal from "components/Buy/NoEmailWarningModal/NoEmailWarningModal";
import { getTextRaLevels } from "../../components/TextRaLevels/TextRaLevels";
import { BuyZustandFormikSync } from "../../components/Buy/BuyZustandFormikSync";

import useSettingsState from "model/useSettingsState";
import useBuyFormValues from "model/useBuyFormValues";
import useParkingSession from "../../model/useParkingSession";
import useTokenSessionState from "model/useTokenSessionState";
import { orderTicket } from "../../model/helpers/Payment";
import { CheckLicencePlate } from "../../model/helpers/CheckLicencePlate";
import { isSafari } from "model/helpers/BrowserHelper";
import styles from "./Buy.module.scss";

import parkingIcon from "../../assets/images/parking.svg";
import pricingIcon from "../../assets/images/pricing.svg";
import parkingMachine from "../../assets/images/parking-machine.svg";
import infoBox from "../../assets/images/info-solid.svg";
import { loadGooglePayScript } from "utils/loadGooglePay";
import { IParkingSession } from "types/vehiclePicker.type";
import TimeSelection from "../../components/Buy/TimeSelection/TimeSelection";
import { useGetParkingMachine } from "../../hooks/api/useGetParkingMachine";
import { ISection } from "../../types/parkingSection.type";
import { IParkingMachine } from "../../types/parkingMachine.type";
import PriceTotal from "components/PriceTotal/PriceTotal";
import { apiTimeExceededErrorMessages, PARK_MACHINE_TYPE } from "../../constants";

const Buy = () => {
    const formikRef = useRef(null);
    const emailInputRef = useRef<HTMLInputElement | null>(null);
    const { t } = useTranslation();
    const lang = i18n.language;
    const navigate = useNavigate();
    const { sectionCode: sectionCodeParam, featureType: featureTypeParam } = useParams();

    const formStore = useBuyFormStore();
    const parkingStore = useParkingInfoStore();
    const uiStore = useBuyUIStore();

    const { cards } = useCardsState();
    const hasCards = cards && cards.length > 0;
    const { nowDate } = useCurrentTime({ timeShiftMs: 5000 });
    const { getAll: getAllParkingSessions, add: addParkingSession } = useParkingSession();

    const { add: addBuyFormValues } = useBuyFormValues();
    const { add: addTokenSession } = useTokenSessionState();
    const { add: addSettingsValue, deleteRecord: deleteSettingsValue } = useSettingsState();

    let sectionData: ISection[] = [];
    let parkingMachineData: IParkingMachine = undefined;

    if (featureTypeParam !== "pz" && sectionCodeParam) {
        const { data: parkingMachineResult } = useGetParkingMachine({ parkMachineCode: sectionCodeParam });
        parkingMachineData = parkingMachineResult;
        const { data: sectionResult } = useGetParkingZone({ sectionCode: parkingMachineResult?.sectionCode });
        sectionData = sectionResult;
    }

    if (featureTypeParam === "pz" && sectionCodeParam) {
        const { data: sectionResult } = useGetParkingZone({ sectionCode: sectionCodeParam });
        sectionData = sectionResult;
    }

    useEffect(() => {
        if (parkingMachineData && parkingMachineData.parkMachineType === PARK_MACHINE_TYPE.infobox && featureTypeParam !== "ib") {
            navigate(`/buy/ib/${sectionCodeParam}`);
        }
        if (parkingMachineData && parkingMachineData.parkMachineType === PARK_MACHINE_TYPE.parkingMachine && featureTypeParam !== "pm") {
            navigate(`/buy/pm/${sectionCodeParam}`);
        }
    }, [parkingMachineData]);

    const {
        data: priceData,
        isError: isGetPriceError,
        isPending: isPriceLoading,
        error: getParkingPriceError,
    } = useGetParkingPrice({
        sectionCode: featureTypeParam === "pz" ? sectionCodeParam : null,
        parkMachineCode: featureTypeParam === "pm" || featureTypeParam === "ib" ? sectionCodeParam : null,
        requestedMinutes: Number(formStore?.requestedMinutes?.value || 15),
        licensePlate: formStore.licencePlate || "INITIAL",
    });

    useEffect(() => {
        uiStore.initSubscriptions();
    }, []);

    useEffect(() => {
        const getZoneDataAsync = async (zone: ISection) => {
            parkingStore.setFeatureType(featureTypeParam as any);
            parkingStore.setSectionCode(sectionCodeParam);
            parkingStore.setParkingZone(zone);

            if (zone.sectionID) {
                const textRaLevels = await getTextRaLevels(zone.sectionID);
                parkingStore.setTextRaLevels(textRaLevels);
            }
        };

        const getParkMachineDataAsync = async (parkingMachineData: IParkingMachine) => {
            parkingStore.setFeatureType(featureTypeParam as any);
            parkingStore.setSectionCode(parkingMachineData.sectionCode);
            parkingStore.setParkingMachine(parkingMachineData);

            if (parkingMachineData.sectionID) {
                const textRaLevels = await getTextRaLevels(parkingMachineData.sectionID);
                parkingStore.setTextRaLevels(textRaLevels);
            }
        };

        if (featureTypeParam && sectionCodeParam) {
            if (parkingMachineData) {
                getParkMachineDataAsync(parkingMachineData);
            }
            if (sectionData && sectionData.length > 0) {
                getZoneDataAsync(sectionData[0]);
            }
            if (sectionData && sectionData.length === 0 && !parkingMachineData) {
                navigate("/");
            }
        }
    }, [featureTypeParam, sectionCodeParam, sectionData]);

    useEffect(() => {
        const calculatePrice = async () => {
            parkingStore.setOriginalPriceForParking(Number(priceData.priceTotal) + Number(priceData.discountAmount));
            parkingStore.setPriceForParking(Number(priceData.priceTotal));
            parkingStore.setToDate(
                priceData.endOfParking
                    ? DateTime.fromISO(priceData.endOfParking)
                    : DateTime.fromISO(priceData.parkingFrom).plus({ minutes: priceData.minutesAccepted })
            );

            const isLimitExceeded = parkingStore.isLimitExceeded();
            uiStore.setDisablePaymentButtonsDueToLimitExceeded(isLimitExceeded);
        };

        priceData && calculatePrice();
        if (isGetPriceError) {
            if (getParkingPriceError && typeof getParkingPriceError === "object") {
                const apiErrorObject: Error = getParkingPriceError as Error;
                if (
                    apiErrorObject.message &&
                    apiTimeExceededErrorMessages.includes(apiErrorObject.message)
                ) {
                    uiStore.setDisablePaymentButtonsDueToLimitExceeded(true);
                }
                if (apiErrorObject.message &&
                    apiErrorObject.message.startsWith("Api Error: No valid parking duration found")) {
                    uiStore.setHasApiError(true);
                    uiStore.setDisablePaymentButtons(true);
                }
            } else {
                uiStore.handleApiError();
            }
        } else {
            uiStore.setHasApiError(false);
            uiStore.setApiErrorMessage(null);
            uiStore.setApiErrorMessageParams(null);
            uiStore.setDisablePaymentButtons(false);
        }
    }, [priceData, isGetPriceError, nowDate, getParkingPriceError]);

    useEffect(() => {
        const checkSession = async () => {
            try {
                const sessionsRes = (await getAllParkingSessions()) as IParkingSession[];
                parkingStore.setSessions(sessionsRes);
            } catch (error) {
                uiStore.handleApiError();
            }
        };

        formStore.licencePlate && parkingStore.sectionCode && checkSession();
    }, [formStore.licencePlate, parkingStore.sectionCode]);

    useEffect(() => {
        if (formStore.licencePlate && parkingStore.sectionCode) {
            parkingStore.checkAndSetCurrentSession(formStore.licencePlate);
        }
    }, [formStore.licencePlate, parkingStore.sectionCode]);

    useEffect(() => {
        if (formStore.licencePlate && CheckLicencePlate(formStore.licencePlate)) {
            formStore.setHasLicencePlateWarning(true);
        } else {
            formStore.setHasLicencePlateWarning(false);
        }
    }, [formStore.licencePlate]);

    const handleOrderTicket = useCallback(
        async (payment: string, cardToken?: string) => {
            const token = cardToken === "new" ? null : cardToken;

            let paymentMethod = payment;
            if (token === null && payment !== "GooglePay" && payment !== "ApplePay") {
                paymentMethod = "Card";
            }

            formStore.setUsePaymentMethod(paymentMethod);
            formStore.setCardToken(cardToken);

            if (payment === "-9") {
                uiStore.setSelectCard(true);
                return;
            }

            await addBuyFormValues({
                dateTime: nowDate,
                values: {
                    licencePlate: formStore.licencePlate,
                    email: formStore.email,
                    requestedMinutes: Number(formStore.requestedMinutes?.value ?? 15),
                },
                featureType: featureTypeParam!,
                sectionCode: featureTypeParam === "pz" && parkingStore.parkingZone?.sectionCode,
            });

            uiStore.setDisablePaymentButtons(true);
            uiStore.setShowLoader(true);

            await deleteSettingsValue("sentToPaymentGateway");
            await addSettingsValue({
                key: "sentToPaymentGateway",
                value: true,
            });

            try {
                await orderTicket(
                    featureTypeParam!,
                    lang,
                    formStore.licencePlate,
                    formStore.email,
                    Number(formStore.requestedMinutes?.value ?? 15),
                    paymentMethod,
                    featureTypeParam === "pz" ? parkingStore.parkingZone : parkingStore.parkingMachine,
                    token,
                    token === null ? null : addTokenSession,
                    parkingStore.priceForParking ?? parkingStore.parkingZone?.tariffInfo?.minPrice,
                    navigate,
                    addParkingSession
                );

            } catch (error) {
                uiStore.handleApiError(error);
            }
        },
        [formStore, parkingStore, uiStore, featureTypeParam, lang, navigate]
    );

    const getPriceBox = () => {
        if (uiStore.disablePaymentButtons || uiStore.disablePaymentButtonsDueToLimitExceeded) return "";
        if (isPriceLoading) return t("global.loading");

        return <PriceTotal
                originalPriceTotal={parkingStore.originalPriceForParking ?? parkingStore.parkingZone?.tariffInfo?.minPrice}
                priceAfterDiscount={parkingStore.priceForParking ?? parkingStore.parkingZone?.tariffInfo?.minPrice}
            />
    }

    const checkNoEmailModal = () => {
        uiStore.setShowNoEmailWarning(!formStore.email);
        return !formStore.email;
    };

    const checkIfHasCards = () => {
        uiStore.setSelectCard(hasCards);
        return hasCards;
    };

    const validationSchema = Yup.object().shape({
        licencePlate: Yup.string().required().min(1).max(10),
        email: Yup.string().matches(/^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/),
        requestedMinutes: Yup.object().required(),
    });

    const initialValues = {
        licencePlate: formStore.licencePlate,
        email: formStore.email,
        requestedMinutes: Number(formStore.requestedMinutes),
    };

    if (uiStore.showLoader) return <FullscreenLoader />;

    if (uiStore.selectCard) {
        return (
            <CardSelection
                cards={cards}
                price={parkingStore.priceForParking ?? parkingStore.parkingZone?.tariffInfo?.minPrice ?? 0}
                disabled={uiStore.disablePaymentButtons || uiStore.disablePaymentButtonsDueToLimitExceeded}
                onBack={() => {
                    uiStore.setSelectCard(false);
                    uiStore.setDisablePaymentButtons(false);
                }}
                onSubmit={(paymentMethod, cardToken) => {
                    handleOrderTicket(paymentMethod, cardToken);
                }}
            />
        );
    }

    return (
        <div className={styles.containerWrapper}>
            {uiStore.showNoEmailWarning && (
                <NoEmailWarningModal
                    paymentMethod={formStore.usePaymentMethod}
                    disablePayment={uiStore.disablePaymentButtons || uiStore.disablePaymentButtonsDueToLimitExceeded}
                    onEnterEmail={() => {
                        uiStore.setSelectCard(false);
                        uiStore.setShowNoEmailWarning(false);
                        setTimeout(() => {
                            emailInputRef.current?.focus();
                        }, 100);
                    }}
                    onPayWithCard={() => {
                        uiStore.setShowNoEmailWarning(false);
                        if (cards.length > 0) {
                            uiStore.setSelectCard(true);
                        } else {
                            handleOrderTicket("Card");
                        }
                    }}
                    onPayWithApple={() => {
                        uiStore.setShowNoEmailWarning(false);
                        handleOrderTicket("ApplePay");
                    }}
                />
            )}

            <div className={styles.container}>
                {/* API Error */}
                {uiStore.hasApiError && (
                    <div className={styles.error}>
                        <p>
                            {uiStore.apiErrorMessage
                                ? t(uiStore.apiErrorMessage, uiStore.apiErrorMessageParams ?? {})
                                : t("buy.apiError")}
                        </p>
                    </div>
                )}
                <Link to={`/${featureTypeParam}/${sectionCodeParam}`} className={styles.back}>
                    <Icon.CaretLeft size={12} color={"#C32B3E"} />
                    &nbsp; {t("buy.back")}
                </Link>

                <h1 className={styles.header}>{t("buy.title")}</h1>

                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    enableReinitialize
                    validationSchema={validationSchema}
                    onSubmit={() => handleOrderTicket("Card")}
                >
                    {({ errors, touched }) => (
                        <Form>
                            {/* Sync Formik <-> Zustand */}
                            <BuyZustandFormikSync />

                            {/* Parking Info */}
                            <div className={styles.item}>
                                <div className={styles.info}>
                                    <div className={styles.icon}>
                                        {featureTypeParam === "pz" ? (
                                            <img alt="Parking" src={parkingIcon} />
                                        ) : featureTypeParam === "ib" ? (
                                            <img alt="Info box" height={24} src={infoBox} />
                                        ) : (
                                            <img alt="Parking machine" height={24} src={parkingMachine} />
                                        )}
                                    </div>
                                    <div className={styles.title}>
                                        <div className={styles.itemTitle}>
                                            {featureTypeParam === "pz" && parkingStore.parkingZone?.sectionCode}
                                            {(featureTypeParam === "pm" || featureTypeParam === "ib") &&
                                                parkingStore.parkingMachine?.parkMachineCode}
                                        </div>
                                    </div>
                                    <div className={styles.title}>
                                        <div className={styles.placeName}>{parkingStore.textRaLevels}</div>
                                    </div>
                                    <div className={styles.textMuted}>
                                        {featureTypeParam === "pz"
                                            ? t("home.MapSearchComponent.zones." + parkingStore.parkingZone?.sectionCategoryName)
                                            : featureTypeParam === "ib"
                                                ? t("home.MapSearchComponent.zones.infoBox")
                                                : t("home.MapSearchComponent.zones.parkingMachine")}
                                    </div>
                                </div>
                            </div>

                            {/* Vehicle Selection */}
                            <div className={styles.item}>
                                <div
                                    className={`${styles.info} ${errors.licencePlate && touched.licencePlate ? styles.inputError : ""
                                        } ${formStore.hasLicencePlateWarning ? styles.inputWarning : ""}`}
                                >
                                    <div className={styles.icon}>
                                        <Icon.Car
                                            color={
                                                errors.licencePlate && touched.licencePlate
                                                    ? "#CC1818"
                                                    : formStore.hasLicencePlateWarning
                                                        ? "#ffa340"
                                                        : "#1D4F8B"
                                            }
                                        />
                                    </div>
                                    <div className={styles.title}>
                                        <div className={styles.itemTitle}>{t("buy.car")}</div>
                                    </div>
                                    <div className={`${styles.itemEnd} ${styles.itemEndExtended} ${styles.inputMobileMax}`}>
                                        <VehiclePicker name="licencePlate" />
                                    </div>
                                </div>
                            </div>

                            {/* Time Selection */}
                            <TimeSelection isPriceLoading={isPriceLoading} />

                            {/* Email Selection */}
                            <div className={styles.item}>
                                <div className={`${styles.info} ${errors.email && touched.email ? styles.inputError : ""}`}>
                                    <div className={styles.icon}>
                                        <Icon.Email color={errors.email && touched.email ? "#CC1818" : "#1D4F8B"} />
                                    </div>
                                    <div className={`${styles.title} ${styles.titleMobile}`}>
                                        <div className={styles.itemTitle}>{t("buy.mail")}</div>
                                    </div>
                                    <div className={`${styles.itemEnd} ${styles.itemEndExtended}`}>
                                        <EmailPicker
                                            inputRef={emailInputRef}
                                            name="email"
                                            hasError={!!(errors.email && touched.email)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Price Info */}
                            <div className={styles.item}>
                                <div className={styles.info}>
                                    <div className={styles.icon}>
                                        <img alt="Pricing" src={pricingIcon} />
                                    </div>
                                    <div className={styles.title}>
                                        <div className={styles.itemTitle}>{t("buy.price")}</div>
                                    </div>
                                    {parkingStore.hasValidTariff() ? (
                                        <div className={styles.text500}>
                                            {getPriceBox()}
                                        </div>
                                    ) : (
                                        <div className={styles.textMuted}>{t("buy.onlyForResidents")}</div>
                                    )}
                                </div>
                            </div>

                            {/* Error Messages */}
                            {((errors.licencePlate && touched.licencePlate) || (errors.email && touched.email)) && (
                                <div className={styles.inputErrorColor}>
                                    <p>{t("buy.formError")}</p>
                                </div>
                            )}

                            {uiStore.disablePaymentButtonsDueToLimitExceeded && (
                                <div className={styles.inputErrorColor}>
                                    <p>{t("buy.parkingSessionLimitExceeded")}</p>
                                </div>
                            )}

                            {formStore.hasLicencePlateWarning && (
                                <div className={styles.inputErrorColor}>
                                    <p>{t("global.licencePlateWarning")}</p>
                                </div>
                            )}

                            {parkingStore.hasValidTariff() && (
                                <div className={styles.buyTicket}>
                                    {isSafari ? (
                                        <button
                                            disabled={
                                                isPriceLoading || uiStore.disablePaymentButtons || uiStore.disablePaymentButtonsDueToLimitExceeded
                                            }
                                            type="button"
                                            onClick={() => {
                                                formStore.setUsePaymentMethod("ApplePay");
                                                if (checkNoEmailModal()) return;
                                                if (checkIfHasCards()) return;

                                                handleOrderTicket("ApplePay");
                                            }}
                                            className={`${styles.buyButton} ${styles.appleButton}`}
                                        >
                                            <Icon.ApplePay className={styles.appleIcon} size={16} />
                                            &nbsp;Pay with Apple
                                        </button>
                                    ) : (
                                        <button
                                            disabled={
                                                isPriceLoading || uiStore.disablePaymentButtons || uiStore.disablePaymentButtonsDueToLimitExceeded
                                            }
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    formStore.setUsePaymentMethod("GooglePay");
                                                    await loadGooglePayScript();
                                                    handleOrderTicket("GooglePay");
                                                } catch (err) {
                                                    console.error(err);
                                                    uiStore.setHasApiError(true);
                                                }
                                            }}
                                            className={`${styles.buyButton} ${styles.googleButton}`}
                                        >
                                            <img src="/images/gpay-bw.svg" alt="GPay" style={{ height: "21px" }} />
                                        </button>
                                    )}

                                    <button
                                        disabled={
                                            isPriceLoading || uiStore.disablePaymentButtons || uiStore.disablePaymentButtonsDueToLimitExceeded
                                        }
                                        type="button"
                                        onClick={() => {
                                            formStore.setUsePaymentMethod("Card");
                                            if (checkNoEmailModal()) return;
                                            if (checkIfHasCards()) return;
                                            handleOrderTicket("Card");
                                        }}
                                        className={styles.buyButton}
                                    >
                                        <Icon.PaymentCard className={styles.actionIcon} size={18} />
                                        &nbsp;&nbsp;{t("buy.payByCard")}
                                    </button>
                                </div>
                            )}
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default Buy;
